import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { decommissionService } from '../services/apiServices';
import { Decommission } from '../types';
import { queryKeys } from '../config/queryClient';

// Get all decommissions with filters
export const useDecommissions = (params?: any) => {
  return useQuery({
    queryKey: queryKeys.decommissions.list(params),
    queryFn: () => decommissionService.getAll(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Get decommission by ID
export const useDecommission = (id: number) => {
  return useQuery({
    queryKey: queryKeys.decommissions.detail(id),
    queryFn: () => decommissionService.getById(id),
    enabled: !!id,
  });
};

// Get decommission statistics
export const useDecommissionStats = () => {
  return useQuery({
    queryKey: queryKeys.decommissions.stats(),
    queryFn: () => decommissionService.getStats(),
    staleTime: 1000 * 60 * 5,
  });
};

// Get decommission trends
export const useDecommissionTrends = (params?: any) => {
  return useQuery({
    queryKey: queryKeys.decommissions.trends(params),
    queryFn: () => decommissionService.getTrends(params),
    staleTime: 1000 * 60 * 5,
  });
};

// Create decommission
export const useCreateDecommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Decommission>) => decommissionService.create(data),
    onSuccess: () => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: queryKeys.decommissions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.birds });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdsStats });
    },
  });
};

// Update decommission
export const useUpdateDecommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Decommission> }) =>
      decommissionService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.decommissions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.decommissions.detail(variables.id) });
    },
  });
};

// Delete decommission
export const useDeleteDecommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => decommissionService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.decommissions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.birds });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdsStats });
    },
  });
};
